import { useRef } from 'react';
import { Camera, X, AlertCircle, Loader2, CheckCircle } from 'lucide-react';

interface StructuredPhotoSlotProps {
  label: string;
  helperText: string;
  required?: boolean;
  /** The completed media key (S3 key) after successful upload */
  value?: string;
  /** Preview URL for the uploaded image (presigned or object URL) */
  previewUrl?: string;
  /** Whether upload is in progress */
  isUploading?: boolean;
  /** Upload progress 0-100 */
  progress?: number;
  error?: string;
  onSelectFile: (file: File) => void;
  onRemove: () => void;
  testId?: string;
}

export function StructuredPhotoSlot({
  label,
  helperText,
  required,
  value,
  previewUrl,
  isUploading,
  progress,
  error,
  onSelectFile,
  onRemove,
  testId,
}: StructuredPhotoSlotProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onSelectFile(file);
    }
    // Reset so same file can be re-selected
    if (inputRef.current) inputRef.current.value = '';
  };

  const hasImage = !!value;

  return (
    <div data-testid={testId}>
      <label className="block text-sm font-medium text-gray-700 mb-1.5">
        <Camera className="w-4 h-4 inline mr-1 text-gray-400" />
        {label} {required && <span className="text-red-500">*</span>}
      </label>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleChange}
        className="hidden"
      />

      {hasImage && previewUrl ? (
        // Preview state
        <div className="relative w-full h-40 bg-gray-100 rounded-lg overflow-hidden border border-gray-200">
          <img
            src={previewUrl}
            alt={label}
            className="w-full h-full object-cover"
          />
          <div className="absolute top-2 right-2 flex items-center gap-1">
            <span className="bg-green-500 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
              <CheckCircle className="w-3 h-3" />
              Ready
            </span>
            <button
              type="button"
              onClick={onRemove}
              className="w-7 h-7 bg-gray-800/70 text-white rounded-full flex items-center justify-center hover:bg-gray-800"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      ) : isUploading ? (
        // Uploading state
        <div className="w-full h-40 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center">
          <Loader2 className="w-8 h-8 text-gray-500 animate-spin mb-2" />
          <p className="text-sm text-gray-600">Uploading... {progress != null ? `${progress}%` : ''}</p>
          <div className="w-32 bg-gray-200 rounded-full h-1.5 mt-2">
            <div
              className="bg-primary-600 h-1.5 rounded-full transition-all"
              style={{ width: `${progress ?? 0}%` }}
            />
          </div>
        </div>
      ) : (
        // Empty state - tap to capture
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className={`w-full h-40 rounded-lg border-2 border-dashed flex flex-col items-center justify-center transition-colors ${
            error
              ? 'border-red-300 bg-red-50 hover:border-red-400'
              : 'border-gray-200 bg-gray-50 hover:border-gray-400 hover:bg-gray-100'
          }`}
        >
          <Camera className={`w-10 h-10 mb-2 ${error ? 'text-red-400' : 'text-gray-400'}`} />
          <p className={`text-sm font-medium ${error ? 'text-red-700' : 'text-gray-700'}`}>
            Tap to take photo or upload
          </p>
        </button>
      )}

      <p className="mt-1 text-xs text-gray-500">{helperText}</p>

      {error && (
        <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />
          {error}
        </p>
      )}
    </div>
  );
}
